import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import moment from 'moment';
import { toast } from 'react-toastify';
import xlsx from 'json-as-xlsx';
import { MultiSelect } from 'react-multi-select-component';
import getMultiSelectStrings from '../../../constants/getMultiSelectStrings';
import DateRangeSelector from '../../../components/DateRangeSelector';
import WarehouseFilter from '../../../components/WarehouseFilter';
import { Warehouse } from '../../stock/models/StockLine';
import {
  ProductCategoryOption,
  productCategoryOptions,
} from '../../stock/models/ProductCategory';
import { listProductVariants } from '../../product-listing/services/ProductVariantCRUD';
import ProductVariantModel from '../../product-listing/models/ProductVartiant';
import {
  getWeightCountOrders,
  WeightCountOrdersResponse,
  WeightCountOrdersDateType,
  CountryWeightData,
} from '../services/DashboardCRUD';
import countryNames from '../../../constants/countryNames';

// Only enforced without a product/category filter (the backend applies the
// same rule).
const MAX_DAYS_RANGE = 45;
const AGGREGATE_KEYS = new Set(['subscriptions', 'shopify', 'b2b']);

function sortEntriesAlphabetically(
  entries: Record<string, string | number>
): [string, string | number][] {
  return Object.entries(entries).sort((a, b) => a[0].localeCompare(b[0]));
}

// A reference with no weight trait only shows up in the units map, so the two
// maps are merged by name instead of iterating the weights alone.
function mergeRecipeEntries(
  weights: Record<string, string>,
  units: Record<string, number>
): [string, string, number | string][] {
  const names = Array.from(
    new Set([...Object.keys(weights), ...Object.keys(units)])
  ).sort((a, b) => a.localeCompare(b));
  return names.map((name) => [name, weights[name] ?? '-', units[name] ?? '-']);
}

function getTabData(
  data: WeightCountOrdersResponse,
  countryCode: string | null
): CountryWeightData {
  if (countryCode) {
    return data[countryCode];
  }
  return {
    subscriptions:
      data.subscriptions as unknown as CountryWeightData['subscriptions'],
    shopify: data.shopify as unknown as CountryWeightData['shopify'],
    b2b: data.b2b as unknown as CountryWeightData['b2b'],
  };
}

interface ProductOption {
  value: number;
  label: string;
}
interface ExcelRow {
  section: string;
  channel: string;
  concept: string;
  orders: number | string;
  weightKg: number | string;
  recipeUnits: number | string;
  units: number | string;
}

function buildTabSheet(
  sheetName: string,
  tabData: CountryWeightData,
  intl: ReturnType<typeof useIntl>
) {
  const summarySection = intl.formatMessage({
    id: 'businessDashboard.ordersWeight.excelSummarySection',
  });
  const recipeSection = intl.formatMessage({
    id: 'businessDashboard.ordersWeight.excelRecipeSection',
  });
  const extraSection = intl.formatMessage({
    id: 'businessDashboard.ordersWeight.excelExtraSection',
  });

  const channels = [
    {
      label: 'B2C',
      orders: tabData.subscriptions.count_orders,
      weightKg: tabData.subscriptions.total_weight_kgs,
      recipeUnits: tabData.subscriptions.total_recipe_units,
      units: tabData.subscriptions.total_extra_products,
      recipes: tabData.subscriptions.recipe_weights,
      recipeUnitsByReference: tabData.subscriptions.recipe_units,
      extras: tabData.subscriptions.extra_products,
    },
    {
      label: 'B2B',
      orders: tabData.b2b.count_b2b,
      weightKg: tabData.b2b.weight_b2b,
      recipeUnits: tabData.b2b.total_recipe_units,
      units: tabData.b2b.total_extra_products,
      recipes: tabData.b2b.recipe_weights_b2b,
      recipeUnitsByReference: tabData.b2b.recipe_units_b2b,
      extras: tabData.b2b.extra_products,
    },
    {
      label: 'Shopify',
      orders: tabData.shopify.count_orders,
      weightKg: tabData.shopify.total_weight_kgs,
      recipeUnits: tabData.shopify.total_recipe_units,
      units: tabData.shopify.total_extra_products,
      recipes: tabData.shopify.recipe_weights,
      recipeUnitsByReference: tabData.shopify.recipe_units,
      extras: tabData.shopify.extra_products,
    },
  ];

  const rows: ExcelRow[] = channels.map((channel) => ({
    section: summarySection,
    channel: channel.label,
    concept: '-',
    orders: channel.orders,
    weightKg: channel.weightKg,
    recipeUnits: channel.recipeUnits,
    units: channel.units,
  }));

  rows.push({
    section: summarySection,
    channel: intl.formatMessage({ id: 'businessDashboard.ordersWeight.total' }),
    concept: '-',
    orders: channels.reduce((acc, channel) => acc + channel.orders, 0),
    weightKg: channels.reduce((acc, channel) => acc + channel.weightKg, 0),
    recipeUnits: channels.reduce(
      (acc, channel) => acc + channel.recipeUnits,
      0
    ),
    units: channels.reduce((acc, channel) => acc + channel.units, 0),
  });

  channels.forEach((channel) => {
    mergeRecipeEntries(channel.recipes, channel.recipeUnitsByReference).forEach(
      ([recipe, weight, recipeUnits]) => {
        rows.push({
          section: recipeSection,
          channel: channel.label,
          concept: recipe,
          orders: '-',
          weightKg: weight,
          recipeUnits,
          units: '-',
        });
      }
    );
  });

  channels.forEach((channel) => {
    sortEntriesAlphabetically(channel.extras).forEach(([product, quantity]) => {
      rows.push({
        section: extraSection,
        channel: channel.label,
        concept: product,
        orders: '-',
        weightKg: '-',
        recipeUnits: '-',
        units: quantity,
      });
    });
  });

  return {
    sheet: sheetName.substring(0, 31),
    columns: [
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.excelSection',
        }),
        value: 'section',
      },
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.excelChannel',
        }),
        value: 'channel',
      },
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.excelConcept',
        }),
        value: 'concept',
      },
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.orderCount',
        }),
        value: 'orders',
      },
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.excelWeight',
        }),
        value: 'weightKg',
      },
      {
        label: intl.formatMessage({ id: 'ordersWeight.recipeUnits' }),
        value: 'recipeUnits',
      },
      {
        label: intl.formatMessage({
          id: 'businessDashboard.ordersWeight.excelUnits',
        }),
        value: 'units',
      },
    ],
    content: rows,
  };
}

interface ExpandableRowProps {
  label: string;
  orderCount: number;
  totalWeight: number;
  recipeWeights: Record<string, string>;
  recipeUnits: Record<string, number>;
  totalRecipeUnits: number;
  extraProducts: Record<string, number>;
  totalExtraProducts: number;
  intl: ReturnType<typeof useIntl>;
}

function ExpandableRow({
  label,
  orderCount,
  totalWeight,
  recipeWeights,
  recipeUnits,
  totalRecipeUnits,
  extraProducts,
  totalExtraProducts,
  intl,
}: ExpandableRowProps) {
  const [expanded, setExpanded] = useState(false);
  const recipes = mergeRecipeEntries(recipeWeights, recipeUnits);
  const extras = sortEntriesAlphabetically(extraProducts) as [string, number][];
  const hasDetails = recipes.length > 0 || extras.length > 0;

  return (
    <>
      <tr
        onClick={hasDetails ? () => setExpanded(!expanded) : undefined}
        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
        className={hasDetails ? 'user-select-none' : ''}
      >
        <td className="px-4">{label}</td>
        <td className="px-4">{orderCount}</td>
        <td className="px-4">
          {totalWeight.toFixed(1)} kg
          {hasDetails && (
            <i
              className="bi bi-chevron-down ms-2 text-dark"
              style={{
                transition: 'transform 0.2s ease',
                transform: expanded ? 'rotate(-180deg)' : 'rotate(0deg)',
                display: 'inline-block',
              }}
            />
          )}
        </td>
        <td className="px-4">{totalRecipeUnits}</td>
        <td className="px-4">{totalExtraProducts}</td>
      </tr>
      {expanded && hasDetails && (
        <tr>
          <td colSpan={5} className="p-4">
            <div className="d-flex gap-8 flex-wrap align-items-start">
              {recipes.length > 0 && (
                <div className="rounded p-4 bg-light">
                  <table className="table table-sm table-bordered mb-0 w-auto">
                    <thead>
                      <tr className="fw-bold">
                        <th className="px-4">
                          {intl.formatMessage({
                            id: 'businessDashboard.ordersWeight.recipe',
                          })}
                        </th>
                        <th className="px-4">
                          {intl.formatMessage({
                            id: 'businessDashboard.ordersWeight.weight',
                          })}
                        </th>
                        <th className="px-4">
                          {intl.formatMessage({
                            id: 'businessDashboard.units',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recipes.map(([recipe, weight, units]) => (
                        <tr key={recipe}>
                          <td className="px-4">{recipe}</td>
                          <td className="px-4">{weight}</td>
                          <td className="px-4">{units}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {extras.length > 0 && (
                <div className="rounded p-4 bg-light">
                  <table className="table table-sm table-bordered mb-0 w-auto">
                    <thead>
                      <tr className="fw-bold">
                        <th className="px-4">
                          {intl.formatMessage({
                            id: 'businessDashboard.product',
                          })}
                        </th>
                        <th className="px-4">
                          {intl.formatMessage({
                            id: 'businessDashboard.units',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {extras.map(([product, quantity]) => (
                        <tr key={product}>
                          <td className="px-4">{product}</td>
                          <td className="px-4">{quantity}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function OrdersWeightReport() {
  const intl = useIntl();
  const [since, setSince] = useState<Date | null>(
    moment().subtract(MAX_DAYS_RANGE, 'days').toDate()
  );
  const [until, setUntil] = useState<Date | null>(new Date());
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [dateType, setDateType] =
    useState<WeightCountOrdersDateType>('promise_date');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<WeightCountOrdersResponse | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
  const [products, setProducts] = useState<ProductVariantModel[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    ProductCategoryOption[]
  >([]);

  const multiSelectStrings = getMultiSelectStrings(intl);

  const isFilteringByProduct =
    selectedProducts.length > 0 || selectedCategories.length > 0;

  const productOptions: ProductOption[] = products
    .map((product) => ({
      value: product.id,
      label: `${product.name} - ${product.ean}`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    async function fetchProducts() {
      try {
        const { data: response } = await listProductVariants();
        setProducts(response);
      } catch (error) {
        console.warn(error);
      }
    }
    fetchProducts();
  }, []);

  async function fetchData() {
    if (!since || !until) {
      toast.error(
        intl.formatMessage({
          id: 'businessDashboard.ordersWeight.selectDateRange',
        })
      );
      return;
    }

    const diffDays = moment(until).diff(moment(since), 'days');
    if (!isFilteringByProduct && diffDays > MAX_DAYS_RANGE) {
      toast.error(
        intl.formatMessage(
          { id: 'businessDashboard.ordersWeight.maxRangeError' },
          { days: MAX_DAYS_RANGE }
        )
      );
      return;
    }

    try {
      setLoading(true);
      const { data: response } = await getWeightCountOrders({
        startDate: moment(since).format('YYYY-MM-DD'),
        endDate: moment(until).format('YYYY-MM-DD'),
        warehouse,
        dateType,
        products: selectedProducts.map((option) => option.value),
        categories: selectedCategories.map((option) => option.value),
      });
      setData(response);
    } catch (error) {
      toast.error(
        intl.formatMessage({ id: 'businessDashboard.ordersWeight.error' })
      );
      console.warn(error);
    } finally {
      setLoading(false);
    }
  }

  function handleDateRangeSelected(newSince: Date, newUntil: Date) {
    const diffDays = moment(newUntil).diff(moment(newSince), 'days');
    if (!isFilteringByProduct && diffDays > MAX_DAYS_RANGE) {
      toast.error(
        intl.formatMessage(
          { id: 'businessDashboard.ordersWeight.maxRangeError' },
          { days: MAX_DAYS_RANGE }
        )
      );
      return;
    }
    setSince(newSince);
    setUntil(newUntil);
  }

  const countryCodes = data
    ? Object.keys(data)
        .filter((key) => !AGGREGATE_KEYS.has(key))
        .sort((a, b) =>
          ((countryNames as Record<string, string>)[a] || a).localeCompare(
            (countryNames as Record<string, string>)[b] || b
          )
        )
    : [];

  const currentData: CountryWeightData | null = data
    ? getTabData(data, selectedCountry)
    : null;

  function handleDownloadExcel() {
    if (!data) {
      return;
    }

    const sheets = [
      buildTabSheet(
        intl.formatMessage({
          id: 'businessDashboard.ordersWeight.allCountries',
        }),
        getTabData(data, null),
        intl
      ),
      ...countryCodes.map((code) =>
        buildTabSheet(
          (countryNames as Record<string, string>)[code] || code,
          getTabData(data, code),
          intl
        )
      ),
    ];

    xlsx(sheets as any, {
      fileName: intl.formatMessage(
        { id: 'businessDashboard.ordersWeight.excelFilename' },
        { datetime: moment().format('YYYY-MM-DD-HH-mm-ss') }
      ),
      extraLength: 3,
      writeMode: 'writeFile',
      writeOptions: {},
    });
  }

  const totalOrders = currentData
    ? currentData.subscriptions.count_orders +
      currentData.b2b.count_b2b +
      currentData.shopify.count_orders
    : 0;
  const totalWeight = currentData
    ? currentData.subscriptions.total_weight_kgs +
      currentData.b2b.weight_b2b +
      currentData.shopify.total_weight_kgs
    : 0;
  const totalRecipeUnits = currentData
    ? currentData.subscriptions.total_recipe_units +
      currentData.b2b.total_recipe_units +
      currentData.shopify.total_recipe_units
    : 0;
  const totalExtraProducts = currentData
    ? currentData.subscriptions.total_extra_products +
      currentData.b2b.total_extra_products +
      currentData.shopify.total_extra_products
    : 0;

  return (
    <>
      <h1 className="title mb-8">
        {intl.formatMessage({ id: 'businessDashboard.ordersWeight.title' })}
      </h1>
      <div className="d-flex flex-wrap gap-4 align-items-end">
        <div>
          <label className="form-label">
            {intl.formatMessage({
              id: 'businessDashboard.ordersWeight.dateTypeLabel',
            })}
          </label>
          <select
            className="form-select"
            value={dateType}
            onChange={(e) =>
              setDateType(e.target.value as WeightCountOrdersDateType)
            }
          >
            <option value="promise_date">
              {intl.formatMessage({
                id: 'businessDashboard.ordersWeight.dateTypePromiseDate',
              })}
            </option>
            <option value="invoice_date">
              {intl.formatMessage({
                id: 'businessDashboard.ordersWeight.dateTypeInvoiceDate',
              })}
            </option>
          </select>
        </div>
        <DateRangeSelector
          initialSince={since}
          initialUntil={until}
          onSelected={handleDateRangeSelected}
          disableFutureDates={false}
          label={intl.formatMessage({
            id:
              dateType === 'invoice_date'
                ? 'businessDashboard.ordersWeight.filterByInvoiceDate'
                : 'businessDashboard.ordersWeight.filterByPromiseDate',
          })}
        />
        <WarehouseFilter value={warehouse} onChange={(w) => setWarehouse(w)} />
        <div className="w-400px">
          <label className="form-label">
            {intl.formatMessage({
              id: 'businessDashboard.ordersWeight.products',
            })}
          </label>
          <MultiSelect
            className="multi-select"
            overrideStrings={multiSelectStrings}
            labelledBy="Select"
            options={productOptions}
            value={selectedProducts}
            onChange={(options: ProductOption[]) =>
              setSelectedProducts(options)
            }
            valueRenderer={(selectedOptions) =>
              selectedOptions.length > 0
                ? selectedOptions.map((option) => option.label).join(', ')
                : intl.formatMessage({ id: 'stock.all' })
            }
          />
        </div>
        <div className="w-250px">
          <label className="form-label">
            {intl.formatMessage({ id: 'stock.category' })}
          </label>
          <MultiSelect
            className="multi-select"
            overrideStrings={multiSelectStrings}
            labelledBy="Select"
            options={productCategoryOptions}
            value={selectedCategories}
            onChange={(options: ProductCategoryOption[]) =>
              setSelectedCategories(options)
            }
            valueRenderer={(selectedOptions) =>
              selectedOptions.length > 0
                ? selectedOptions.map((option) => option.label).join(', ')
                : intl.formatMessage({ id: 'stock.all' })
            }
          />
        </div>
        <button
          className="btn btn-primary mb-1"
          onClick={fetchData}
          disabled={loading || !since || !until}
        >
          {loading ? (
            <span className="spinner-border spinner-border-sm me-2" />
          ) : null}
          {intl.formatMessage({
            id: 'businessDashboard.ordersWeight.generateReport',
          })}
        </button>
        {data && (
          <button
            className="btn btn-secondary mb-1"
            onClick={handleDownloadExcel}
          >
            <i className="fas fa-file-excel me-2" />
            {intl.formatMessage({
              id: 'businessDashboard.ordersWeight.downloadExcel',
            })}
          </button>
        )}
      </div>
      {!isFilteringByProduct && (
        <div className="text-muted small mt-2">
          {intl.formatMessage(
            { id: 'businessDashboard.ordersWeight.maxRangeHint' },
            { days: MAX_DAYS_RANGE }
          )}
        </div>
      )}
      {dateType === 'invoice_date' && (
        <div className="text-muted small mt-2">
          {intl.formatMessage({
            id: 'businessDashboard.ordersWeight.invoiceDateB2bOnlyNote',
          })}
        </div>
      )}

      {!loading && data && currentData && (
        <div className="d-flex gap-4">
          <section
            className="card shadow mt-8 p-10"
            style={{ width: 'fit-content' }}
          >
            <ul className="nav nav-tabs mb-6">
              <li className="nav-item">
                <a
                  className={`nav-link ${selectedCountry === null ? 'active' : ''}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedCountry(null);
                  }}
                >
                  {intl.formatMessage({
                    id: 'businessDashboard.ordersWeight.allCountries',
                  })}
                </a>
              </li>
              {countryCodes.map((code) => (
                <li className="nav-item" key={code}>
                  <a
                    className={`nav-link ${selectedCountry === code ? 'active' : ''}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedCountry(code);
                    }}
                  >
                    {(countryNames as Record<string, string>)[code] || code}
                  </a>
                </li>
              ))}
            </ul>
            <table className="table table-bordered table-striped">
              <thead>
                <tr className="fw-bolder">
                  <th className="px-4">
                    {intl.formatMessage({
                      id: 'businessDashboard.ordersWeight.type',
                    })}
                  </th>
                  <th className="px-4">
                    {intl.formatMessage({
                      id: 'businessDashboard.ordersWeight.orderCount',
                    })}
                  </th>
                  <th className="px-4">
                    {intl.formatMessage({ id: 'ordersWeight.recipeWeight' })}
                  </th>
                  <th className="px-4">
                    {intl.formatMessage({ id: 'ordersWeight.recipeUnits' })}
                  </th>
                  <th className="px-4">
                    {intl.formatMessage({ id: 'ordersWeight.extraProducts' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                <ExpandableRow
                  label="B2C"
                  orderCount={currentData.subscriptions.count_orders}
                  totalWeight={currentData.subscriptions.total_weight_kgs}
                  recipeWeights={currentData.subscriptions.recipe_weights}
                  recipeUnits={currentData.subscriptions.recipe_units}
                  totalRecipeUnits={
                    currentData.subscriptions.total_recipe_units
                  }
                  extraProducts={currentData.subscriptions.extra_products}
                  totalExtraProducts={
                    currentData.subscriptions.total_extra_products
                  }
                  intl={intl}
                />
                <ExpandableRow
                  label="B2B"
                  orderCount={currentData.b2b.count_b2b}
                  totalWeight={currentData.b2b.weight_b2b}
                  recipeWeights={currentData.b2b.recipe_weights_b2b}
                  recipeUnits={currentData.b2b.recipe_units_b2b}
                  totalRecipeUnits={currentData.b2b.total_recipe_units}
                  extraProducts={currentData.b2b.extra_products}
                  totalExtraProducts={currentData.b2b.total_extra_products}
                  intl={intl}
                />
                <ExpandableRow
                  label="Shopify"
                  orderCount={currentData.shopify.count_orders}
                  totalWeight={currentData.shopify.total_weight_kgs}
                  recipeWeights={currentData.shopify.recipe_weights}
                  recipeUnits={currentData.shopify.recipe_units}
                  totalRecipeUnits={currentData.shopify.total_recipe_units}
                  extraProducts={currentData.shopify.extra_products}
                  totalExtraProducts={currentData.shopify.total_extra_products}
                  intl={intl}
                />
              </tbody>
              <tfoot>
                <tr className="fw-bold">
                  <td className="px-4">
                    {intl.formatMessage({
                      id: 'businessDashboard.ordersWeight.total',
                    })}
                  </td>
                  <td className="px-4">{totalOrders}</td>
                  <td className="px-4">{totalWeight.toFixed(1)} kg</td>
                  <td className="px-4">{totalRecipeUnits}</td>
                  <td className="px-4">{totalExtraProducts}</td>
                </tr>
              </tfoot>
            </table>
          </section>
        </div>
      )}
    </>
  );
}
